import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  VStack,
  ZStack,
  Logo,
} from "@abodeminehq/parcel-ui";
import { useAutoSignIn } from "@abodeminehq/entitlements-client-react";
import { createFileRoute } from "@tanstack/react-router";
import { useNavigate } from "@tanstack/react-router";
import * as React from "react";
import { AnimatedBackground } from "~/components/brand/animated-background";
import { useAuthContext } from "~/contexts/auth-context";
import { LogtoScopes } from "~/lib/logto/logto";

export const Route = createFileRoute("/")({
  component: HomePage,
});

function HomePage() {
  const {
    isAuthenticated,
    logtoClient,
    isLoading,
    claims,
    user,
    accessToken,
    signOut,
    signIn,
  } = useAuthContext();
  const navigate = useNavigate();

  // A signed-out arrival goes to the IdP rather than to a button: sign-in
  // happens once, at accounts.abodemine.com, and anyone still holding that
  // session is redirected out and back without seeing a thing. `?signin=1` —
  // where sign-out lands — is what asks for this screen itself.
  const signingIn = useAutoSignIn({ signIn, isAuthenticated, isLoading });

  const hasAdminAccess = React.useMemo(() => {
    return claims?.scopes.includes(LogtoScopes.AdminAccess);
  }, [claims]);

  const hasOrgAccess = React.useMemo(() => {
    return (user?.organizations?.length ?? 0) > 0;
  }, [user]);

  React.useEffect(() => {
    if (isAuthenticated && hasAdminAccess) {
      // Redirect to admin dashboard if authenticated
      navigate({ to: "/admin" });
    } else if (isAuthenticated && !hasAdminAccess) {
      // check out if the user has access to any orgs
      // console.log("Org data", user?.organization_data)
      if ((user?.organizations?.length ?? 0) > 0) {
        // Get last org id for this user from local storage safely
        const rawLastOrgId = localStorage.getItem("lastOrgId");
        let lastOrgIdObj: { userId?: string; orgId?: string } = {};
        if (rawLastOrgId) {
          try {
            const parsed = JSON.parse(rawLastOrgId);
            if (parsed && typeof parsed === "object") {
              lastOrgIdObj = parsed;
            }
          } catch {
            // Ignore invalid JSON and fall back to first available org
            lastOrgIdObj = {};
          }
        }

        const userOrganizations = user?.organizations ?? [];
        const canUseLastOrg =
          lastOrgIdObj.userId === user?.id &&
          typeof lastOrgIdObj.orgId === "string" &&
          userOrganizations.includes(lastOrgIdObj.orgId);

        const targetOrgId = canUseLastOrg
          ? lastOrgIdObj.orgId
          : userOrganizations[0];

        if (targetOrgId) {
          navigate({ to: `/org/${targetOrgId}` });
        }
      }
    }
  }, [isAuthenticated, navigate, isLoading, hasAdminAccess, user]);

  // const hasOrgAccess = React.useMemo(() => {
  //   return user?.organization_data?.some(org => org.))
  // }, [user])

  // Show loading state while auth is being initialized
  if (isLoading || signingIn) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated && hasAdminAccess) {
    // Show loading while redirecting
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-muted-foreground">Redirecting...</p>
        </div>
      </div>
    );
  } else if (isAuthenticated && !hasAdminAccess && !hasOrgAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="mx-auto mb-2">
              <Logo className="[&_img]:h-10" />
            </div>
            <CardTitle>No Access</CardTitle>
          </CardHeader>
          <CardContent>
            <VStack spacing={4} className="items-stretch">
              <p className="text-sm text-muted-foreground text-center">
                You do not have access to any organizations. Please contact your
                administrator to get access.
              </p>

              <Accordion type="single" collapsible>
                <AccordionItem value="item-1">
                  <AccordionTrigger>Claims</AccordionTrigger>
                  <AccordionContent>
                    <pre className="text-xs break-all whitespace-pre-wrap">
                      {JSON.stringify(claims, null, 2)}
                    </pre>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-2">
                  <AccordionTrigger>Access Token</AccordionTrigger>
                  <AccordionContent>
                    <pre className="text-xs break-all whitespace-pre-wrap">
                      {accessToken}
                    </pre>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

              <Button className="w-full" onClick={() => signOut()}>
                Sign Out
              </Button>
            </VStack>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <ZStack> 
      <div className="absolute inset-0 w-screen h-screen">
      <AnimatedBackground  />
      </div>
      <div className="min-h-screen flex items-center justify-center">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <Logo className="[&_img]:h-18" />
          {/* <h1 className="text-3xl font-bold mb-2">API Portal</h1> */}
          <p className="text-muted-foreground mb-8">
            Sign in to access the API dashboard
          </p>
        </div>

        <Button
        size={"lg"}
          onClick={() => signIn()}
          className="w-full bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition-colors font-medium"
        >
          Sign In
        </Button>
        
      </div>
    </div>
    </ZStack>
   
  );
}
